type ResizeCallback = (entry: ResizeObserverEntry) => void

let observer: ResizeObserver | null = null
const callbacksByTarget = new Map<Element, ResizeCallback>()

const getObserver = (): ResizeObserver => {
	observer ??= new ResizeObserver((entries) => {
		for (const entry of entries) {
			callbacksByTarget.get(entry.target)?.(entry)
		}
	})

	return observer
}

/**
 * Observes resizes of a single element via one shared `ResizeObserver` instance instead of creating a new
 * observer per caller. Keeps the number of active observers (and their per-frame notification work) low,
 * which matters when many resize-based animations (e.g. `AnimatedStage`) are mounted on the same page and
 * unmount together (e.g. during route navigation), where separate observers can otherwise cause
 * "ResizeObserver loop completed with undelivered notifications" warnings.
 */
export const observeResize = (target: Element, callback: ResizeCallback): (() => void) => {
	callbacksByTarget.set(target, callback)
	getObserver().observe(target)

	return () => {
		callbacksByTarget.delete(target)
		observer?.unobserve(target)

		// tear down the singleton once nothing is being observed anymore, instead of keeping a live
		// ResizeObserver around indefinitely (also avoids order-dependence between tests that stub `ResizeObserver`)
		if (callbacksByTarget.size === 0) {
			observer?.disconnect()
			observer = null
		}
	}
}
